import React, { useState, useEffect } from 'react';
import { X, Clock, Plus, Trash2 } from 'lucide-react';
import { schedulesAPI, taxonomyAPI, workflowsAPI, usersAPI } from '../../utils/api';
import API from '../../utils/api';
import toast from 'react-hot-toast';

export default function PMSchedulesModal({ onClose }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  
  const [form, setForm] = useState({
    asset_type_id: '',
    criticality: 'medium',
    intervalValue: 1,
    intervalUnit: 'months',
    workflow_template_id: '',
    department: '',
    start_date: '',
    end_date: ''
  });
  const [departments, setDepartments] = useState([]);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [schRes, typeRes, wfRes, userRes] = await Promise.all([
        schedulesAPI.list(),
        taxonomyAPI.types(),
        workflowsAPI.list(),
        usersAPI.list() // Use usersAPI instead of direct API call for consistency
      ]);
      setSchedules(schRes.data.schedules);
      setTypes(typeRes.data.types);
      setWorkflows(wfRes.data.workflows);
      const deps = [...new Set(userRes.data.users.map(u => u.department).filter(Boolean))];
      setDepartments(deps);
    } catch (err) {
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.asset_type_id || !form.intervalValue) return toast.error('Asset Type and Interval are required');
    let days = parseInt(form.intervalValue);
    if (form.intervalUnit === 'weeks') days *= 7;
    if (form.intervalUnit === 'months') days *= 30;

    setCreating(true);
    try {
      await schedulesAPI.create({
        asset_type_id: form.asset_type_id,
        criticality: form.criticality,
        frequency_days: days,
        workflow_template_id: form.workflow_template_id,
        department: form.department,
        start_date: form.start_date || null,
        end_date: form.end_date || null
      });
      toast.success('Schedule created');
      setForm({ ...form, asset_type_id: '', workflow_template_id: '', start_date: '', end_date: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create schedule');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if(!window.confirm('Delete this schedule?')) return;
    try {
      await schedulesAPI.delete(id);
      setSchedules(schedules.filter(s => s.id !== id));
      toast.success('Schedule deleted');
    } catch (err) {
      toast.error('Failed to delete schedule');
    }
  };

  const currentType = types.find(t => t.id === form.asset_type_id);
  const typeCategoriesText = currentType ? `(Category: ${currentType.category_name})` : '';

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="modal-title">Preventive Maintenance Schedules</div>
            <div style={{ fontSize:13, color:'var(--text-muted)', marginTop:2 }}>Define repeating job intervals for your asset classification matrix.</div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:20 }}>
          {/* Create Form */}
          <div style={{ background:'var(--bg-muted)', padding:16, borderRadius:'var(--r-lg)', border:'1px solid var(--border)' }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:12, color:'var(--text)', display:'flex', alignItems:'center', gap:6 }}>
              <Clock size={16} color="var(--accent)" /> Add New Schedule Rule
            </div>
            
            <div className="form-row" style={{ gridTemplateColumns:'2fr 1fr' }}>
              <div className="form-group">
                <label className="form-label">Asset Type {typeCategoriesText}</label>
                <select className="form-select" value={form.asset_type_id} onChange={e => setForm({...form, asset_type_id: e.target.value})}>
                  <option value="">Select Asset Type...</option>
                  {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Target Criticality</label>
                <select className="form-select" value={form.criticality} onChange={e => setForm({...form, criticality: e.target.value})}>
                  <option value="critical">Critical Only</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            <div className="form-row" style={{ gridTemplateColumns:'1fr 1.5fr' }}>
              <div className="form-group">
                <label className="form-label">Run Interval</label>
                <div style={{ display:'flex', gap:8 }}>
                  <input type="number" min="1" className="form-input" style={{ width: 80 }} value={form.intervalValue} onChange={e => setForm({...form, intervalValue: e.target.value})} />
                  <select className="form-select" style={{ flex:1 }} value={form.intervalUnit} onChange={e => setForm({...form, intervalUnit: e.target.value})}>
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Job Template Workflow (Optional)</label>
                <select className="form-select" value={form.workflow_template_id} onChange={e => setForm({...form, workflow_template_id: e.target.value})}>
                  <option value="">No specific template</option>
                  {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Target Department</label>
                <select className="form-select" value={form.department} onChange={e => setForm({...form, department: e.target.value})}>
                  <option value="">All Departments</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Active From (Optional)</label>
                <input type="date" className="form-input" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Active Until (Optional)</label>
                <input type="date" className="form-input" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} />
              </div>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:12 }}>
              <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={creating || !form.asset_type_id}>
                <Plus size={14} /> Add Rule
              </button>
            </div>
          </div>

          {/* List existing rules */}
          <div>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'var(--text)' }}>Active Master Schedules</div>
            
            {loading ? (
               <div style={{ padding:20, display:'flex', justifyContent:'center' }}><div className="spinner" style={{ width:24, height:24 }} /></div>
            ) : schedules.length === 0 ? (
               <div style={{ padding:20, textAlign:'center', color:'var(--text-muted)', fontSize:13, background:'var(--bg)', borderRadius:'var(--r-md)', border:'1px dashed var(--border)' }}>
                 No maintenance schedules defined yet. Add a rule above to automate PM jobs!
               </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {schedules.map(sch => (
                  <div key={sch.id} style={{ display:'flex', alignItems:'center', background:'var(--bg-card)', padding:'12px 16px', borderRadius:'var(--r-md)', border:'1px solid var(--border)' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14, fontWeight:600, color:'var(--text)' }}>
                        {sch.asset_type_name} <span style={{ color:'var(--text-muted)', fontWeight:400, fontSize:12 }}>({sch.criticality} criticality)</span>
                      </div>
                      <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4, display:'flex', alignItems:'center', gap:6 }}>
                        <Clock size={12} color="var(--accent)" /> 
                        Runs every {sch.frequency_days} days
                        {sch.department && <span style={{ padding:'2px 6px', background:'var(--bg-muted)', borderRadius:4, marginLeft:8 }}>Dept: {sch.department}</span>}
                        {sch.workflow_template_name && <span style={{ padding:'2px 6px', background:'var(--bg-muted)', borderRadius:4, marginLeft:8 }}>Template: {sch.workflow_template_name}</span>}
                        {(sch.start_date || sch.end_date) && (
                          <span style={{ fontSize:10, color:'var(--text-muted)', marginLeft:8 }}>
                            [Window: {sch.start_date ? new Date(sch.start_date).toLocaleDateString() : '∞'} - {sch.end_date ? new Date(sch.end_date).toLocaleDateString() : '∞'}]
                          </span>
                        )}
                      </div>
                    </div>
                    <button className="btn-icon" onClick={() => handleDelete(sch.id)} style={{ color:'var(--danger)' }}><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
